/**
 * 학생 진도 추이 페이지
 * 특정 학생의 특정 문제집에 대한 시간별 진도 추이 표시
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Space, Button, Alert, Spin, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { ProgressTrendChart } from '../../components/stats/ProgressTrendChart';
import { getStudentProgressTrend } from '../../services/statsService';
import { StudentProgressTrendResponse } from '../../types/stats';

const { Title } = Typography;

export const StudentProgressPage: React.FC = () => {
  const { studentId, workbookName } = useParams<{
    studentId: string;
    workbookName: string;
  }>();
  const navigate = useNavigate();

  const [progressData, setProgressData] = useState<StudentProgressTrendResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 진도 추이 데이터 로드
  const loadProgress = async () => {
    if (!studentId || !workbookName) {
      setError('학생 ID 또는 문제집 이름이 없습니다.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getStudentProgressTrend(studentId, workbookName);
      setProgressData(data);
    } catch (err: any) {
      console.error('진도 추이 로드 실패:', err);
      setError(err.response?.data?.detail || '진도 추이를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, workbookName]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            돌아가기
          </Button>
        </div>
        <Title level={3} style={{ margin: 0 }}>
          {progressData?.student_name}님 - {workbookName} 진도 추이
        </Title>
      </div>

      {/* 에러 메시지 */}
      {error && <Alert message="에러" description={error} type="error" showIcon closable />}

      {/* 로딩 스피너 */}
      {loading && !progressData ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* 진도 추이 차트 */}
          {progressData && (
            <ProgressTrendChart progress={progressData.progress} loading={loading} />
          )}
        </>
      )}
    </Space>
  );
};

export default StudentProgressPage;
